import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig, isAuthenticated, logout } from '../utils/auth';
import '../App.css';
import AppNav from '../components/AppNav';

function DowngradeSubscriptionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { service } = useParams();
  const { newPlan, currentSubscription } = location.state || {};

  const [isDowngrading, setIsDowngrading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);
  const [loadingCredit, setLoadingCredit] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate(`/${service}`);
      return;
    }
    if (!newPlan || !currentSubscription) {
      navigate(`/${service}`);
      return;
    }
    // Fetch credit info
    const fetchCredit = async () => {
      setLoadingCredit(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/subscriptions/upgrade-credit?planSlug=${encodeURIComponent(newPlan.slug)}&currentSubId=${encodeURIComponent(currentSubscription.id)}`,
          getAuthConfig()
        );
        setCreditInfo(res.data);
      } catch (err) {
        // Non-fatal — just don't show credit breakdown
        console.error('Could not load credit info:', err);
      } finally {
        setLoadingCredit(false);
      }
    };
    fetchCredit();
  }, [navigate, newPlan, currentSubscription, service]);

  const handleConfirmDowngrade = async () => {
    try {
      setIsDowngrading(true);
      setError(null);

      await axios.post(
        `${API_URL}/api/subscriptions/confirm-downgrade`,
        {
          currentSubId: currentSubscription.id,
          newPlanSlug: newPlan.slug,
          domain: currentSubscription.domain,
          domainType: currentSubscription.domainType,
          subdomain: currentSubscription.subdomain,
        },
        getAuthConfig()
      );

      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm downgrade. Please try again.');
      setIsDowngrading(false);
    }
  };

  const handleCancel = () => {
    navigate('/subscriptions');
  };

  if (!newPlan || !currentSubscription) {
    return null;
  }

  if (success) {
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={() => {}} />
        <div className="email-entry-container">
          <div className="email-entry-card">
            <div style={{textAlign: 'center'}}>
              <i className="fa-solid fa-clock" style={{fontSize: '64px', color: '#40E0D0', marginBottom: '24px', filter: 'drop-shadow(0 0 20px rgba(64, 224, 208, 0.4))'}}></i>
              <h1 className="email-entry-title">Downgrade Requested</h1>
              <p className="email-entry-subtitle" style={{marginBottom: '16px'}}>
                Your downgrade request from <strong>{currentSubscription?.planName || 'your current plan'}</strong> to <strong>{newPlan.name}</strong> has been received.
              </p>
              <p className="email-entry-subtitle" style={{marginBottom: '32px', fontSize: '14px', color: '#888'}}>
                Our team will review and apply the change shortly. You'll receive a confirmation email once it's been processed.
                {creditInfo?.creditCents > 0 && ` A credit of $${(creditInfo.creditCents / 100).toFixed(2)} will be applied to your account upon approval.`}
              </p>
              <button onClick={() => navigate('/subscriptions')} className="btn-continue" style={{maxWidth: '280px', margin: '0 auto'}}>
                <i className="fa-solid fa-arrow-left" style={{marginRight: 6}}></i> View My Subscriptions
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const planPrice = creditInfo?.newPlanPriceCents != null
    ? creditInfo.newPlanPriceCents / 100
    : (newPlan.price || 0);
  const creditAmt = creditInfo?.creditCents ? creditInfo.creditCents / 100 : 0;
  const netCharge = creditInfo?.netChargeCents != null
    ? creditInfo.netChargeCents / 100
    : planPrice;

  return (
    <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
      <div className="email-entry-container">
        <div className="email-entry-card">
          <div className="email-entry-icon">
            <i className="fa-solid fa-arrow-down"></i>
          </div>
          <h1 className="email-entry-title">Confirm Downgrade</h1>
          <p className="email-entry-subtitle">
            Review the details of your plan change
          </p>

          {error && (
            <div className="alert alert-error" style={{marginBottom: '20px'}}>
              {error}
            </div>
          )}

          {/* Plan Change */}
          <div style={{
            background: 'rgba(64, 224, 208, 0.05)',
            border: '1px solid rgba(64, 224, 208, 0.2)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px'
          }}>
            <h3 style={{fontSize: '16px', fontWeight: '600', color: '#ffffff', marginBottom: '16px'}}>Plan Change</h3>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', fontSize: '14px'}}>
              <div>
                <div style={{color: '#999', fontSize: '12px', marginBottom: '4px'}}>Current Plan</div>
                <div style={{color: '#ffffff', fontWeight: '600'}}>{currentSubscription.planName}</div>
              </div>
              <i className="fa-solid fa-arrow-right" style={{color: '#40E0D0'}}></i>
              <div style={{textAlign: 'right'}}>
                <div style={{color: '#999', fontSize: '12px', marginBottom: '4px'}}>New Plan</div>
                <div style={{color: '#40E0D0', fontWeight: '600'}}>
                  {newPlan.name}
                  <span style={{color: '#999', fontWeight: 'normal', marginLeft: '8px'}}>
                    ${planPrice.toFixed(2)}/mo
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Credit Breakdown */}
          {loadingCredit ? (
            <div style={{textAlign: 'center', color: '#666', fontSize: '13px', padding: '12px 0', marginBottom: '16px'}}>Loading credit info…</div>
          ) : creditAmt > 0 ? (
            <div style={{
              background: 'rgba(16, 185, 129, 0.08)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '10px'}}>
                <i className="fa-solid fa-circle-check" style={{color: '#10b981', fontSize: '16px', marginTop: '2px'}}></i>
                <div style={{flex: 1}}>
                  <p style={{margin: '0 0 10px 0', color: '#10b981', fontSize: '14px', fontWeight: '600'}}>Credit from remaining time</p>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: '#aaa'}}>New plan price</span>
                      <span style={{color: '#fff'}}>${planPrice.toFixed(2)}</span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: '#10b981'}}>Your credit</span>
                      <span style={{color: '#10b981'}}>−${creditAmt.toFixed(2)}</span>
                    </div>
                    <div style={{borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px', display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: '#fff', fontWeight: '600'}}>First invoice</span>
                      <span style={{color: '#fff', fontWeight: '600'}}>{netCharge <= 0 ? 'No charge' : `$${netCharge.toFixed(2)}`}</span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{color: '#aaa', fontSize: '12px'}}>Ongoing</span>
                      <span style={{color: '#aaa', fontSize: '12px'}}>${planPrice.toFixed(2)}/month</span>
                    </div>
                  </div>
                  {netCharge <= 0 && (
                    <p style={{margin: '10px 0 0 0', color: '#10b981', fontSize: '13px', fontStyle: 'italic'}}>
                      Your credit fully covers the first invoice — no charge today.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Domain */}
          {currentSubscription.domain && (
            <div style={{
              background: 'rgba(64, 224, 208, 0.05)',
              border: '1px solid rgba(64, 224, 208, 0.2)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <i className="fa-solid fa-globe" style={{color: '#40E0D0', fontSize: '16px'}}></i>
                <div>
                  <p style={{margin: '0 0 2px 0', color: '#ccc', fontSize: '13px'}}>Domain carried over</p>
                  <p style={{margin: '0', color: '#40E0D0', fontSize: '14px', fontWeight: '600'}}>{currentSubscription.domain}</p>
                </div>
              </div>
            </div>
          )}

          <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            <button onClick={handleConfirmDowngrade} className="btn-continue" disabled={isDowngrading}>
              {isDowngrading ? (
                <><span className="spinner"></span>Processing…</>
              ) : (
                <><i className="fa-solid fa-check-circle"></i> Confirm Downgrade</>
              )}
            </button>
            <button onClick={handleCancel} className="btn-continue" disabled={isDowngrading}
              style={{background: '#2d2d2d', color: '#cccccc', border: '2px solid #4d4d4d'}}>
              Cancel
            </button>
          </div>

          <p className="email-entry-note" style={{marginTop: '24px'}}>
            Your new plan activates immediately. Admin will review your service configuration.
          </p>
        </div>
      </div>
    </div>
  );
}

export default DowngradeSubscriptionPage;
